const isInAppWebView = () =>
  /wv\)|WebView|MavenTech|MavenConnect/i.test(navigator.userAgent);

const getUserMedia = (constraints) => {
  if (navigator.mediaDevices?.getUserMedia) {
    return navigator.mediaDevices.getUserMedia(constraints);
  }

  const legacyGetUserMedia =
    navigator.getUserMedia ||
    navigator.webkitGetUserMedia ||
    navigator.mozGetUserMedia;

  if (!legacyGetUserMedia) {
    return Promise.reject(new Error("Camera is not supported on this device."));
  }

  return new Promise((resolve, reject) => {
    legacyGetUserMedia.call(navigator, constraints, resolve, reject);
  });
};

export function getCameraBlockedMessage() {
  if (isInAppWebView()) {
    return "Camera is blocked inside the Maven app browser. Ask the mobile team to enable WebView camera access, or open this page in Chrome.";
  }

  return "Camera is blocked. Tap Allow when prompted, or enable Camera for this site in browser settings.";
}

export function getCameraErrorMessage(error) {
  const name = error?.name || "";

  if (name === "NotAllowedError" || name === "PermissionDeniedError") {
    return getCameraBlockedMessage();
  }

  if (name === "NotFoundError" || name === "DevicesNotFoundError") {
    return "No camera found on this device.";
  }

  if (name === "NotReadableError" || name === "TrackStartError") {
    return "Camera is already in use. Close other camera apps and retry.";
  }

  if (name === "OverconstrainedError") {
    return "Could not start camera with the selected settings. Tap Retry Camera.";
  }

  return error?.message || "Unable to start camera.";
}

export async function requestCameraAccess() {
  if (!window.isSecureContext) {
    throw new Error("Camera requires a secure connection (HTTPS).");
  }

  const attempts = [
    { video: { facingMode: { ideal: "environment" } }, audio: false },
    { video: { facingMode: "environment" }, audio: false },
    { video: { facingMode: "user" }, audio: false },
    { video: true, audio: false },
  ];

  let lastError = new Error("Unable to access camera.");

  for (const constraints of attempts) {
    try {
      const stream = await getUserMedia(constraints);
      stream.getTracks().forEach((track) => track.stop());
      return;
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError;
}
